import React from 'react';
import styled from 'styled-components';
import { SelectTerms } from '../shared/SelectTerms';
import { useTranslation } from 'react-i18next';
import { useSlideData } from '@context/slide-data';
import { SliderContainer, TermExplanation, TermLabel, SlideSection, SectionHeader } from '../styles';
import { SectionLabel, SummaryTable } from '../shared';
import { useQueryClient } from 'react-query';
import { Checklist, ChecklistContainer } from '../styles';
import { useClient } from '@context/presentation';
import { QUERY_KEYS, UNAVAILABLE_CS_FIRM } from '@constants';

const SummaryStyle = styled.div`
    height: 100%;
    width: 100%
`;

export const Summary = () => {
    const queryClient = useQueryClient();
    const { firmShortName } = useClient();
    const { t } = useTranslation();
    const { currentSlideQuery } = QUERY_KEYS;
    const { data, slideState, sectionsVisible, desktop } = useSlideData();
    const { id, details: options } = data;
    const { availableTerms, language } = slideState;
    const selectedTerm = slideState.selectedTerm[id];
    const isSpanish = language === 'Spanish';
        
    if (firmShortName === UNAVAILABLE_CS_FIRM) {
        return <div>Information not available</div>
    }

    // To-do: reload automatically after a timeout
    // Safeguard to prevent crashing just in case CA data and TP data get out of sync
    if (availableTerms.length && !availableTerms.includes(selectedTerm)) {
        queryClient.invalidateQueries([currentSlideQuery, id], {
            refetchInactive: true
        });
    
        return <div>Reloading term options...please refresh if terms don&apos;t reload within a few seconds.</div>;
    }

    // For scripts. Only the rep's slideState reducer includes the 'selected section' field
    const selectedScriptSection = slideState.selectedSection?.[id];

    const { currentSituation, proposedSituations } = options;
    const newMonthlyPayment = proposedSituations[selectedTerm].proposedTerms.newMonthlyPayment;
    
    return (
        <SummaryStyle>
            <SlideSection
                visible={sectionsVisible}
                selected={selectedScriptSection === 1}>
                    <SectionLabel section={1} />
                    <SectionHeader>{t('summary.current_balance')}</SectionHeader>
                    <SummaryTable 
                        currentSituation={currentSituation}
                        proposedSituations={proposedSituations}
                        selectedTerm={selectedTerm}
                    />
                    <label htmlFor='summary-term-box'>
                        <SectionHeader>{t('summary.proposed_terms')}</SectionHeader>
                    </label>
                    <SliderContainer desktop={desktop} marginBottom={isSpanish ? '.8rem' : '1.8rem'}>
                        <SelectTerms 
                            value={selectedTerm}
                            options={proposedSituations}
                        /> 
                        <TermExplanation desktop={desktop}>
                            <TermLabel htmlFor='new-payment'>
                                {t('term_options.new_payment')}
                            </TermLabel>
                            <h2 id='new-payment'>{newMonthlyPayment}</h2>
                        </TermExplanation>
                    </SliderContainer>
            </SlideSection>
            <SlideSection>
                <Highlights
                    desktop={desktop}
                    selectedTerm={selectedTerm} 
                    proposedSituations={proposedSituations} />
            </SlideSection>
        </SummaryStyle>
    );
};

const HighlightsStyle = styled(Checklist)`
    font-size: ${props => props.theme.smallFont};
    grid-template-rows: ${props => (props.desktop && props.gridRows) ?? '1fr 1fr 1fr'};
    height: auto;
    position: relative;
    ${props => props.marginBottom && 'margin-bottom: 2rem;'}
    li {
        ${props => props.marginRight && 'margin-right: 25px;'}
        font-size: ${props => props.theme.mediumFont};
    }
    li::before {
        content: '•';
        position: absolute;
        left: -0.8em;  
        font-size: 1.1em;
    }
    li, li::before {
        margin-left: 20px;
    }
`;

function Highlights({ selectedTerm, proposedSituations, desktop }) {
    const selectedSituation = proposedSituations[selectedTerm];
    const { t } = useTranslation();
    const { savings, termDescription } = selectedSituation;
    const { monthlySavings, totalSavings, annualSavings } = savings;

    return (
        <div>
            <SectionHeader margin={desktop ? '10px 0' : '20px 0'}>
                {t('summary.highlights.header')}
            </SectionHeader>
            <ChecklistContainer desktop={desktop}>
                <HighlightsStyle
                    desktop={desktop}
                    gridRows='1fr 1fr'
                    marginRight={true}>
                        <li>{t('summary.highlights.monthly_1')}
                                <strong>{monthlySavings}</strong>
                            {t('summary.highlights.monthly_2')}
                        </li>
                        <li>{t('summary.highlights.years_saved')}
                            <strong>{termDescription} {t('global.months')}</strong>.
                        </li>
                </HighlightsStyle>
                <HighlightsStyle desktop={desktop} marginBottom={desktop ? false : true}>
                    <li>{t('summary.highlights.monthly_payment')}</li>
                    <li>{t('summary.highlights.total_saved')}
                        <strong>{totalSavings}</strong>.
                    </li>
                    <li>
                        {t('summary.highlights.annual_saved')}
                        <strong>{annualSavings}</strong>.
                    </li>
                </HighlightsStyle>
            </ChecklistContainer>
        </div>
    );
}

export default Summary;
